import { useState } from 'react'

import { api } from '../api/client'
import type { EscalationPolicy, Matcher, Page, RoutePolicy, Severity, Team } from '../api/types'
import { Card, Empty, ErrorNote, Spinner, TableScroll } from '../components/ui'
import { useAction, useQuery } from '../hooks/useApi'
import { useAuth } from '../auth/AuthContext'

const OPERATORS: Matcher['operator'][] = ['eq', 'neq', 'regex', 'not_regex', 'in', 'exists']
const SEVERITIES: Severity[] = ['critical', 'high', 'warning', 'info']

interface Draft {
  id?: string
  name: string
  description: string
  priority: number
  is_active: boolean
  matchers: Matcher[]
  severities: Severity[]
  team_id: string | null
  escalation_policy_id: string | null
  group_by: string
  continue_matching: boolean
}

const EMPTY: Draft = {
  name: '',
  description: '',
  priority: 100,
  is_active: true,
  matchers: [{ label: 'service', operator: 'eq', value: '' }],
  severities: [],
  team_id: null,
  escalation_policy_id: null,
  group_by: '',
  continue_matching: false,
}

export function RoutesPage() {
  const { can } = useAuth()
  const routes = useQuery(() => api.get<Page<RoutePolicy>>('/routes', { limit: 200 }), [])
  const teams = useQuery(() => api.get<Page<Team>>('/teams', { limit: 200 }), [])
  const policies = useQuery(
    () => api.get<Page<EscalationPolicy>>('/escalation-policies', { limit: 200 }),
    [],
  )
  const action = useAction()
  const [draft, setDraft] = useState<Draft | null>(null)

  const writable = can('policies.write')

  const save = async () => {
    if (!draft) return
    const body = {
      ...draft,
      // Значение matcher-а `exists` — булево, остальные операторы работают со строкой.
      matchers: draft.matchers.map((matcher) =>
        matcher.operator === 'exists'
          ? { ...matcher, value: matcher.value === true || matcher.value === 'true' }
          : matcher.operator === 'in'
            ? { ...matcher, value: String(matcher.value ?? '').split(',').map((v) => v.trim()).filter(Boolean) }
            : matcher,
      ),
      group_by: draft.group_by.split(',').map((v) => v.trim()).filter(Boolean),
    }
    const ok = await action.run(() =>
      draft.id ? api.put(`/routes/${draft.id}`, body) : api.post('/routes', body),
    )
    if (ok) {
      setDraft(null)
      routes.reload()
    }
  }

  const remove = async (route: RoutePolicy) => {
    if (await action.run(() => api.del(`/routes/${route.id}`))) routes.reload()
  }

  const edit = (route: RoutePolicy) =>
    setDraft({
      id: route.id,
      name: route.name,
      description: route.description,
      priority: route.priority,
      is_active: route.is_active,
      matchers: route.matchers.length ? route.matchers : EMPTY.matchers,
      severities: route.severities,
      team_id: route.team_id,
      escalation_policy_id: route.escalation_policy_id,
      group_by: route.group_by.join(', '),
      continue_matching: route.continue_matching,
    })

  const teamName = (id: string | null) =>
    teams.data?.items.find((team) => team.id === id)?.name ?? '—'
  const policyName = (id: string | null) =>
    policies.data?.items.find((policy) => policy.id === id)?.name ?? '—'

  return (
    <>
      <ErrorNote>{routes.error ?? action.error}</ErrorNote>

      <Card
        title="Правила маршрутизации"
        actions={
          writable && (
            <button className="btn-primary" onClick={() => setDraft({ ...EMPTY })}>
              Новое правило
            </button>
          )
        }
      >
        {routes.loading && !routes.data ? (
          <Spinner />
        ) : (routes.data?.items.length ?? 0) === 0 ? (
          <Empty>
            Правил нет — алерты не будут назначены команде и не запустят эскалацию.
          </Empty>
        ) : (
          <TableScroll>
            <thead>
              <tr>
                <th className="th">Приоритет</th>
                <th className="th">Название</th>
                <th className="th">Условия</th>
                <th className="th">Команда</th>
                <th className="th">Эскалация</th>
                <th className="th">Группировка</th>
                <th className="th" />
              </tr>
            </thead>
            <tbody>
              {routes.data!.items.map((route) => (
                <tr key={route.id} className={route.is_active ? '' : 'opacity-50'}>
                  <td className="td tabular-nums">{route.priority}</td>
                  <td className="td">
                    {route.name}
                    {route.continue_matching && (
                      <span className="ml-2 text-[11px] text-slate-500">continue</span>
                    )}
                  </td>
                  <td className="td">
                    <div className="space-y-0.5 font-mono text-[11px] text-slate-300">
                      {route.matchers.map((matcher, index) => (
                        <div key={index}>
                          {matcher.label} {matcher.operator} {JSON.stringify(matcher.value)}
                        </div>
                      ))}
                      {route.severities.length > 0 && (
                        <div className="text-slate-500">severity ∈ {route.severities.join(', ')}</div>
                      )}
                    </div>
                  </td>
                  <td className="td">{teamName(route.team_id)}</td>
                  <td className="td">{policyName(route.escalation_policy_id)}</td>
                  <td className="td font-mono text-[11px]">{route.group_by.join(', ') || '—'}</td>
                  <td className="td text-right">
                    {writable && (
                      <div className="flex gap-1 justify-end">
                        <button className="btn-ghost text-xs" onClick={() => edit(route)}>
                          Изменить
                        </button>
                        <button className="btn-danger text-xs" onClick={() => remove(route)}>
                          Удалить
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableScroll>
        )}
      </Card>

      {draft && (
        <Card
          title={draft.id ? 'Изменение правила' : 'Новое правило'}
          actions={
            <>
              <button className="btn-ghost" onClick={() => setDraft(null)}>
                Отмена
              </button>
              <button className="btn-primary" disabled={action.pending} onClick={save}>
                Сохранить
              </button>
            </>
          }
        >
          <div className="p-4 grid lg:grid-cols-2 gap-4">
            <div>
              <label className="label">Название</label>
              <input
                className="input"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Приоритет (меньше — важнее)</label>
              <input
                className="input"
                type="number"
                value={draft.priority}
                onChange={(e) => setDraft({ ...draft, priority: Number(e.target.value) })}
              />
            </div>

            <div>
              <label className="label">Команда</label>
              <select
                className="input"
                value={draft.team_id ?? ''}
                onChange={(e) => setDraft({ ...draft, team_id: e.target.value || null })}
              >
                <option value="">—</option>
                {teams.data?.items.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Политика эскалации</label>
              <select
                className="input"
                value={draft.escalation_policy_id ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, escalation_policy_id: e.target.value || null })
                }
              >
                <option value="">—</option>
                {policies.data?.items.map((policy) => (
                  <option key={policy.id} value={policy.id}>
                    {policy.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="lg:col-span-2">
              <label className="label">Условия (все должны совпасть)</label>
              <div className="space-y-2">
                {draft.matchers.map((matcher, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      className="input flex-1"
                      placeholder="метка"
                      value={matcher.label}
                      onChange={(e) => {
                        const matchers = [...draft.matchers]
                        matchers[index] = { ...matcher, label: e.target.value }
                        setDraft({ ...draft, matchers })
                      }}
                    />
                    <select
                      className="input w-32"
                      value={matcher.operator}
                      onChange={(e) => {
                        const matchers = [...draft.matchers]
                        matchers[index] = {
                          ...matcher,
                          operator: e.target.value as Matcher['operator'],
                        }
                        setDraft({ ...draft, matchers })
                      }}
                    >
                      {OPERATORS.map((operator) => (
                        <option key={operator} value={operator}>
                          {operator}
                        </option>
                      ))}
                    </select>
                    <input
                      className="input flex-1"
                      placeholder={matcher.operator === 'in' ? 'a, b, c' : 'значение'}
                      value={String(matcher.value ?? '')}
                      onChange={(e) => {
                        const matchers = [...draft.matchers]
                        matchers[index] = { ...matcher, value: e.target.value }
                        setDraft({ ...draft, matchers })
                      }}
                    />
                    <button
                      className="btn-ghost"
                      onClick={() =>
                        setDraft({
                          ...draft,
                          matchers: draft.matchers.filter((_, i) => i !== index),
                        })
                      }
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  className="btn-ghost text-xs"
                  onClick={() =>
                    setDraft({
                      ...draft,
                      matchers: [...draft.matchers, { label: '', operator: 'eq', value: '' }],
                    })
                  }
                >
                  Добавить условие
                </button>
              </div>
            </div>

            <div>
              <label className="label">Severity (пусто — любые)</label>
              <div className="flex flex-wrap gap-2">
                {SEVERITIES.map((severity) => (
                  <button
                    key={severity}
                    className={`btn text-xs ${
                      draft.severities.includes(severity) ? 'bg-sky-600 text-white' : 'btn-ghost'
                    }`}
                    onClick={() =>
                      setDraft({
                        ...draft,
                        severities: draft.severities.includes(severity)
                          ? draft.severities.filter((s) => s !== severity)
                          : [...draft.severities, severity],
                      })
                    }
                  >
                    {severity}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label">Группировать по меткам (через запятую)</label>
              <input
                className="input"
                placeholder="service, cluster"
                value={draft.group_by}
                onChange={(e) => setDraft({ ...draft, group_by: e.target.value })}
              />
              <p className="mt-1 text-xs text-slate-500">
                Алерты с одинаковыми значениями этих меток попадут в один инцидент.
              </p>
            </div>

            <div className="lg:col-span-2 flex gap-6 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={draft.is_active}
                  onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                />
                Активно
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={draft.continue_matching}
                  onChange={(e) => setDraft({ ...draft, continue_matching: e.target.checked })}
                />
                Продолжать проверку следующих правил
              </label>
            </div>
          </div>
        </Card>
      )}
    </>
  )
}
